import { useEffect, useState } from 'react'
import type { RepositoryAction, RunnerRepositorySettings as Settings } from '../api/types'
import { errorMessage } from '../api/client'
import { useUnsavedChanges } from '../components/unsaved'
import { toast } from '../components/toasts'
import { useRunners, useSessionsEnv } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'

export function RunnerRepositorySettings({ project }: { project: string }) {
  const store = useStore()
  const { me, offline } = useBoardState()
  const { runners } = useRunners()
  const [selected, select] = useState('')
  if (store.byToken || !me?.member) return null
  const listed = (runners ?? []).filter(r => !r.revoked)
  const runner = listed.some(r => r.id === selected) ? selected : listed[0]?.id ?? ''
  return <>
    <label className="field"><span>Runner</span><select value={runner} onChange={e => select(e.target.value)}>
      {!listed.length && <option value="">No runners</option>}
      {listed.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
    </select></label>
    <p className="muted">These settings apply to new sessions on this machine.</p>
    {runner && <RepositoryMachine key={`${runner}/${project}`} runner={runner} project={project} unavailable={offline || !listed.find(r => r.id === runner)?.connected} />}
  </>
}
function RepositoryMachine({ runner, project, unavailable }: { runner: string; project: string; unavailable: boolean }) {
  const { api } = useSessionsEnv()
  const [answer, setAnswer] = useState<Settings | null>(null)
  const [attempt, retry] = useState(0)
  const [problem, setProblem] = useState<string | null>(null)
  const [reading, setReading] = useState(false)
  useEffect(() => {
    let active = true
    setAnswer(null); setProblem(null)
    if (unavailable || !api.repositorySettings) { setProblem('The runner is unavailable.'); return }
    setReading(true)
    api.repositorySettings(runner, { project, action: { action: 'read' } }).then(value => { if (active) setAnswer(value) }, error => { if (active) setProblem(errorMessage(error)) }).finally(() => { if (active) setReading(false) })
    return () => { active = false }
  }, [api, runner, project, unavailable, attempt])
  return <>
    <button type="button" className="button button-small" disabled={reading || unavailable} onClick={() => retry(n => n + 1)}>Refresh repository</button>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
    {answer && <RepositoryFields key={attempt} answer={answer} unavailable={unavailable || reading} send={async action => {
      if (!api.repositorySettings) throw new Error('The runner is unavailable.')
      return api.repositorySettings(runner, { project, action })
    }} />}
  </>
}
function RepositoryFields({ answer, unavailable, send }: { answer: Settings; unavailable: boolean; send: (action: RepositoryAction) => Promise<Settings> }) {
  const [held, setHeld] = useState(answer.preferences)
  const [draft, setDraft] = useState(answer.preferences)
  const [found, setFound] = useState(answer.inspection)
  const [busy, setBusy] = useState(false)
  const [uncertain, setUncertain] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const dirty = JSON.stringify(draft) !== JSON.stringify(held)
  useUnsavedChanges(dirty)
  const needsInitial = found && (!found.repository || !found.has_commit)
  const needsInspection = draft.checkout !== held.checkout && !found
  async function inspect() {
    if (!draft.checkout || busy || unavailable || uncertain) return
    setBusy(true); setProblem(null)
    try {
      const read = await send({ action: 'inspect', folder: draft.checkout })
      setFound(read.inspection)
      if (read.inspection) setDraft(d => ({ ...d, checkout: read.inspection!.folder, remote: read.inspection!.remote, default_branch: read.inspection!.default_branch ?? d.default_branch }))
    } catch (error) { setProblem(errorMessage(error)) }
    finally { setBusy(false) }
  }
  async function save(initialise: boolean) {
    if (busy || unavailable || uncertain || needsInspection || (!initialise && needsInitial)) return
    setBusy(true); setProblem(null)
    try {
      const saved = await send({ action: 'save', expected: held, preferences: draft, initialise })
      setHeld(saved.preferences); setDraft(saved.preferences); setFound(saved.inspection); toast('Saved.')
    } catch (error) { setProblem(`${errorMessage(error)} Refresh before trying again.`); setUncertain(true) }
    finally { setBusy(false) }
  }
  return <form className="setup-form" onSubmit={e => { e.preventDefault(); void save(false) }}>
    <fieldset className="settings-group" disabled={busy || unavailable || uncertain}>
      <legend>Code and worktrees</legend>
      <label className="field"><span>Its code on this machine</span><input value={draft.checkout ?? ''} maxLength={4096} autoComplete="off" spellCheck={false} onChange={e => { setDraft({ ...draft, checkout: e.target.value || null }); setFound(null) }} /></label>
      <button type="button" className="button" disabled={!draft.checkout} onClick={() => void inspect()}>Read repository</button>
      {found && <>
        <label className="field"><span>Remote</span><select value={draft.remote ?? ''} onChange={e => setDraft({ ...draft, remote: e.target.value || null })}>
          <option value="">None</option>
          {found.remotes.map(remote => <option key={remote.name} value={remote.name}>{remote.name}</option>)}
        </select></label>
        <div className="field"><span>Remote address</span><span>{found.remotes.find(r => r.name === draft.remote)?.address || 'None'}</span></div>
      </>}
      <label className="field"><span>Default branch on this machine</span><input value={draft.default_branch} maxLength={200} onChange={e => setDraft({ ...draft, default_branch: e.target.value })} /></label>
      <label className="field"><span>Root folder</span><input value={draft.root} maxLength={4096} onChange={e => setDraft({ ...draft, root: e.target.value })} /></label>
      <label className="field"><span>Branch name</span><input value={draft.branch} maxLength={200} onChange={e => setDraft({ ...draft, branch: e.target.value })} /></label>
      <label className="field"><span>Folder name</span><input value={draft.folder} maxLength={256} onChange={e => setDraft({ ...draft, folder: e.target.value })} /></label>
      <p className="muted">Use {'{project}'}, {'{issue}'}, {'{slug}'} and {'{session}'}. Leave Branch name empty for a detached worktree.</p>
      {needsInitial && <div className="settings-accept-band"><p>{found.repository ? 'This repository has no commit yet.' : 'This folder is not a Git repository. Agents need one to work in it.'}</p><p className="muted">The first commit will be empty.</p><button type="button" className="button" onClick={() => void save(true)}>{found.repository ? 'Make the first commit' : 'Make it a Git repository'}</button></div>}
      <div className="form-actions">
        <button type="button" className="button button-quiet" onClick={() => setDraft({ ...draft, root: answer.default_root, folder: '{session}', branch: '' })}>Restore defaults</button>
        <button className="button" disabled={!dirty || !draft.checkout || !draft.root || !draft.folder || !!needsInitial || needsInspection}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </fieldset>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
  </form>
}
